import { Card, Grid } from "@mui/material";
import type { DateRange } from "@mui/x-date-pickers-pro";
import { Methods } from "api";
import { TYPE_ERROR } from "common/constants/common";
import { INITIAL_FEATURE_FLAG_VALUES } from "common/constants/initialValues";
import * as MESSAGES from "common/constants/messages";
import { CLAIM_STATUS } from "common/constants/options";
import type { CSVData, CSVHeader, ColumnData, FeatureFlags, FilterData, TypeClaim } from "common/types/commonTypes";
import ProDataGrid from "components/proDataGrid/ProDataGrid";
import { ProDataGridProvider } from "components/proDataGrid/ProDataGridProvider";
import dayjs, { type Dayjs } from "dayjs";
import { useAPI, useAuth, usePopups } from "hooks";
import { isEmpty } from "lodash";
import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Claim, ModelClaimFilterInputNew, OrgUserResponse } from "s12-library/types";
import { mapCSVData, mapTableData, tableColumnList } from "utils/commonFunctions";
import DuplicatedDetails from "./DuplicatedDetails";

const DuplicatedWrapper = () => (
  <ProDataGridProvider>
    <Duplicated />
  </ProDataGridProvider>
);

const Duplicated: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDetails, setShowDetails] = useState(false);
  const [rowData, setRowData] = useState<TypeClaim[]>([]);
  const [clickedRowId, setClickedRowId] = useState("");
  const [searchData, setSearchData] = useState<FilterData | null>(null);
  const [yearFilterData, setYearFilterData] = useState<ColumnData | null>(null);
  const [ccgFilterData, setCcgFilterData] = useState<ColumnData | null>(null);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([dayjs().subtract(1, "day"), dayjs()]);
  const [csvData, setCsvData] = useState<[CSVHeader[], CSVData[]]>([[], []]);
  const [selectedCsvData, setSelectedCsvData] = useState<[CSVHeader[], CSVData[]]>([[], []]);
  const { handleBannerMessage } = usePopups();

  const {
    data: duplicatedClaimData,
    loading: duplicatedClaimLoading,
    error: duplicatedClaimError,
    trigger: getDuplicatedClaim,
  } = useAPI<Claim[], { filter: ModelClaimFilterInputNew }>({
    method: Methods.GET,
    fieldName: "listClaims",
    manual: true,
  });

  const {
    data: dateRangeData,
    loading: dateRangeDataLoading,
    error: dateRangeDataError,
    trigger: getDateRangeData,
  } = useAPI<Claim[], { filter: ModelClaimFilterInputNew }>({
    method: Methods.GET,
    fieldName: "listClaims",
    manual: true,
    args: {
      filter: {
        status: CLAIM_STATUS.duplicated,
        receivedDateFrom: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
        receivedDateTo: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
      },
    },
  });

  const {
    data: ccgData,
    loading: ccgLoading,
    error: ccgError,
    trigger: getCcg,
  } = useAPI<OrgUserResponse[], { id: string }>({
    method: Methods.GET,
    fieldName: "getClaimUserOrgs",
    manual: true,
  });

  const ccgList = useMemo(() => ccgData?.map((item) => ({ key: item.id, value: item.name })) ?? [], [ccgData]);

  useEffect(() => {
    setRowData(duplicatedClaimData && !duplicatedClaimLoading ? duplicatedClaimData.map(mapTableData) : []);
    return () => setRowData([]);
  }, [duplicatedClaimData, duplicatedClaimLoading]);

  const getClaims = useCallback(() => {
    if (!user) {
      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
      return;
    }
    return getDuplicatedClaim({
      filter: {
        status: CLAIM_STATUS.duplicated,
        ...(searchData && { searchValue: searchData.searchString }),
        ...(ccgFilterData && { claimOrganisationId: ccgFilterData.key }),
        ...(yearFilterData && { searchYear: yearFilterData.key }),
      },
    });
  }, [ccgFilterData, getDuplicatedClaim, handleBannerMessage, searchData, user, yearFilterData]);

  useEffect(() => {
    getClaims();
  }, [getClaims]);

  useEffect(() => {
    if (user) getCcg({ id: user.username });
  }, [getCcg, user]);

  useEffect(() => {
    if (dateRange) getDateRangeData();
  }, [dateRange, getDateRangeData]);

  useEffect(() => {
    if (duplicatedClaimError || ccgError || dateRangeDataError) {
      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
    }
  }, [ccgError, dateRangeDataError, duplicatedClaimError, handleBannerMessage]);

  const ccg = useMemo(() => ccgData ?? [], [ccgData]);
  const featuresFromAllOrgs = useMemo(
    () =>
      ccg
        .map((o) => JSON.parse(o?.featureFlags ?? INITIAL_FEATURE_FLAG_VALUES) as FeatureFlags)
        .reduce(
          (acc, curr) => ({
            ...acc,
            ...Object.entries(curr)
              .filter(([_, value]) => !!value)
              .reduce((a, value) => ({ ...a, [value[0]]: value[1] }), {}),
          }),
          {} as FeatureFlags,
        ),
    [ccg],
  );
  const currentOrg = useMemo(
    () => (ccgFilterData ? ccg.find((o) => o.id === ccgFilterData.key) : ({} as OrgUserResponse)),
    [ccg, ccgFilterData],
  );
  const featuresFromCurrentOrg = useMemo(
    () =>
      !isEmpty(JSON.parse(currentOrg?.featureFlags ?? INITIAL_FEATURE_FLAG_VALUES) as FeatureFlags)
        ? (JSON.parse(currentOrg?.featureFlags ?? INITIAL_FEATURE_FLAG_VALUES) as FeatureFlags)
        : featuresFromAllOrgs,
    [currentOrg?.featureFlags, featuresFromAllOrgs],
  );

  useEffect(() => {
    if (dateRangeData) setCsvData(mapCSVData(dateRangeData as Parameters<typeof mapCSVData>[0]));
  }, [dateRangeData]);

  const setSearchDataValue = useCallback((value: FilterData) => setSearchData(value), []);

  useEffect(() => {
    if (location.search.length > 0) {
      setClickedRowId(decodeURIComponent(location.search.substring(1)));
      setShowDetails(true);
      navigate(location.pathname, {
        state: { isSameMenu: false, isInDetailsPage: true },
      });
    }
  }, [location.pathname, location.search, navigate]);

  return (
    <>
      {showDetails && (
        <DuplicatedDetails
          rowId={clickedRowId}
          goBack={() => {
            setShowDetails(false);
            getClaims();
          }}
        />
      )}
      {!showDetails && (
        <Grid
          m={1}
          p={2}
          component={Card}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            borderRadius: 1,
            minHeight: window.innerHeight - 85,
          }}>
          <Grid container m={0}>
            <Grid container spacing={3} sx={{ height: "fit-content" }}>
              <Grid item xs={12}>
                <ProDataGrid
                  title="Duplicated"
                  rows={rowData}
                  columns={tableColumnList(
                    featuresFromAllOrgs,
                    featuresFromCurrentOrg,
                    ccgFilterData?.length > 0 ? ccgFilterData?.key : undefined,
                  )}
                  dataError={!duplicatedClaimData && !duplicatedClaimLoading}
                  ccgList={ccgList}
                  filterData={searchData}
                  filterCcgData={ccgFilterData}
                  filterYearData={yearFilterData}
                  csvData={csvData}
                  selectedCsvData={selectedCsvData}
                  dateRangeLoading={dateRangeDataLoading}
                  button={{}}
                  dateRange={setDateRange}
                  loading={duplicatedClaimLoading}
                  ccgLoading={ccgLoading}
                  refetchData={() => {
                    setSearchData(null);
                    setYearFilterData(null);
                  }}
                  debouncedSearchString={setSearchDataValue}
                  ccgValue={setCcgFilterData}
                  yearValue={setYearFilterData}
                  selectedRows={(rowIds) => {
                    if (duplicatedClaimData) {
                      setSelectedCsvData(mapCSVData(duplicatedClaimData as Parameters<typeof mapCSVData>[0], rowIds));
                    }
                  }}
                  onClickRowData={(event) => {
                    setClickedRowId(event.id);
                    setShowDetails(true);
                    navigate(location.pathname, {
                      state: { isSameMenu: false, isInDetailsPage: true },
                    });
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default memo(DuplicatedWrapper);
